import { useEffect, useState, useMemo } from 'react';
import type { IssueExecution } from '../../hooks/useSprintTelemetry';
import type { SprintEpic } from '../../api/sprint';
import type { ActiveSprite } from '../../api/coder';
import type { TelemetryEvent } from '../../api/telemetry';
import { useSpriteActivity } from '../../api/telemetry';

interface SprintKPIRowProps {
  issueExecutions: IssueExecution[];
  epics: SprintEpic[];
  sprites: ActiveSprite[];
  startedAt?: string;
  completedAt?: string;
  failedAt?: string;
  sprintStatus?: string;
  telemetryEvents?: TelemetryEvent[];
  topErrorMessage?: string;
  batchId?: string;
  /** Authoritative story count from sprint plan (overrides issueExecutions.length) */
  totalStoriesOverride?: number;
}

interface KpiCard {
  label: string;
  value: string;
  sub?: string;
  accent: 'coral' | 'green' | 'red' | 'blue' | 'neutral';
  pulse?: boolean;
}

const accentColors: Record<string, { border: string; value: string; bg: string }> = {
  coral:   { border: 'border-l-coral-500', value: 'text-coral-600', bg: 'bg-coral-50/40' },
  green:   { border: 'border-l-accent-500', value: 'text-accent-700', bg: 'bg-accent-50/40' },
  red:     { border: 'border-l-red-500', value: 'text-red-600', bg: 'bg-red-50/40' },
  blue:    { border: 'border-l-blue-500', value: 'text-blue-700', bg: 'bg-blue-50/40' },
  neutral: { border: 'border-l-neutral-300', value: 'text-neutral-600', bg: 'bg-neutral-50/40' },
};

function formatElapsed(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  if (ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function formatStaticElapsed(startedAt: string, endedAt: string): string {
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function SprintKPIRow({ issueExecutions, epics, sprites, startedAt, completedAt, failedAt, sprintStatus, telemetryEvents = [], topErrorMessage, batchId, totalStoriesOverride }: SprintKPIRowProps) {
  // Initialize elapsed immediately from props — no flash of "—"
  const computeInitialElapsed = (): string => {
    if (!startedAt) return '—';
    const endTime = completedAt || failedAt;
    if (endTime && (sprintStatus === 'completed' || sprintStatus === 'failed' || sprintStatus === 'partial')) {
      return formatStaticElapsed(startedAt, endTime);
    }
    return formatElapsed(startedAt);
  };

  const [elapsed, setElapsed] = useState(computeInitialElapsed);

  useEffect(() => {
    if (!startedAt) { setElapsed('—'); return; }

    const endTime = completedAt || failedAt;
    if (endTime && (sprintStatus === 'completed' || sprintStatus === 'failed' || sprintStatus === 'partial')) {
      setElapsed(formatStaticElapsed(startedAt, endTime));
      return;
    }

    setElapsed(formatElapsed(startedAt));
    const id = setInterval(() => setElapsed(formatElapsed(startedAt)), 1000);
    return () => clearInterval(id);
  }, [startedAt, completedAt, failedAt, sprintStatus]);

  // Use plan total when available (issueExecutions only has started stories)
  const totalStories = totalStoriesOverride || issueExecutions.length;
  const completedStories = issueExecutions.filter((e) => e.status === 'success').length;
  const totalEpics = epics.length;
  const completedEpics = epics.filter((e) => e.status === 'completed').length;
  const activeSprites = sprites.filter((s) => s.status === 'running' || s.status === 'started').length;
  const errorCount = issueExecutions.filter((e) => e.status === 'failure').length;

  const storiesProgress = totalStories > 0 ? Math.round((completedStories / totalStories) * 100) : 0;

  // Derive commits count from telemetry events + story execution data
  const commitCount = useMemo(() => {
    // Count explicit commit_made events
    let count = telemetryEvents.filter((e) => e.eventType === 'commit_made').length;
    // If no explicit commit events, derive from story execution commits
    if (count === 0) {
      count = issueExecutions.reduce((sum, exec) => sum + (exec.commits?.length || 0), 0);
    }
    return count;
  }, [telemetryEvents, issueExecutions]);

  // Derive verification stats from telemetry events, with story execution fallback
  const verificationStats = useMemo(() => {
    const verifications = telemetryEvents.filter((e) => e.eventType === 'verification_completed');
    if (verifications.length > 0) {
      const passed = verifications.filter((e) => (e.payload as Record<string, unknown>)?.passed === true).length;
      const failed = verifications.length - passed;
      return { total: verifications.length, passed, failed };
    }
    // Fallback: count completed story executions as QA checks
    const completed = issueExecutions.filter((e) => e.status === 'success' || e.status === 'failure');
    const passed = completed.filter((e) => e.status === 'success').length;
    const failed = completed.filter((e) => e.status === 'failure').length;
    return { total: completed.length, passed, failed };
  }, [telemetryEvents, issueExecutions]);

  // Full-cycle KPIs
  const buildStats = useMemo(() => {
    const completed = telemetryEvents.filter((e) => e.eventType === 'build_completed');
    const succeeded = completed.filter((e) => !(e.payload as Record<string, unknown>)?.error).length;
    return { total: completed.length, succeeded };
  }, [telemetryEvents]);

  const e2eStats = useMemo(() => {
    // Count test_started events for total tests initiated
    const started = telemetryEvents.filter((e) => e.eventType === 'test_started').length;
    // Count test completions that passed (repair_completed with finalFailed=0, or build_completed with failed=0)
    const completedTests = telemetryEvents.filter((e) => {
      if (e.eventType === 'repair_completed') {
        const p = e.payload as Record<string, unknown>;
        return p?.finalFailed === 0;
      }
      if (e.eventType === 'build_completed') {
        const p = e.payload as Record<string, unknown>;
        return (p?.failed === 0) && !p?.error;
      }
      return false;
    }).length;
    return { started, allPass: completedTests };
  }, [telemetryEvents]);

  const repairCount = useMemo(() => {
    return telemetryEvents.filter((e) => e.eventType === 'repair_started').length;
  }, [telemetryEvents]);

  // Sprite activity KPIs — Files Changed + Quality Gates
  const spriteActivity = useSpriteActivity(batchId, { enabled: !!batchId });
  const fileStats = useMemo(() => {
    const events = spriteActivity.data?.events || [];
    const created = events.filter(e => e.event_type === 'sprite_file_created').length;
    const modified = events.filter(e => e.event_type === 'sprite_file_modified').length;
    return { total: created + modified, created, modified };
  }, [spriteActivity.data?.events]);

  const qualityGateStats = useMemo(() => {
    const events = spriteActivity.data?.events || [];
    const passed = events.filter(e => e.event_type === 'sprite_lint_passed' || e.event_type === 'sprite_typecheck_passed').length;
    const failed = events.filter(e => e.event_type === 'sprite_lint_failed' || e.event_type === 'sprite_typecheck_failed').length;
    const total = passed + failed;
    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
    return { total, passed, failed, passRate };
  }, [spriteActivity.data?.events]);

  const cards: KpiCard[] = [
    {
      label: 'Stories',
      value: totalStories > 0 ? `${completedStories}/${totalStories}` : '—',
      sub: totalStories > 0 ? `${storiesProgress}% executed` : undefined,
      accent: completedStories === totalStories && totalStories > 0 ? 'green' : 'coral',
    },
    {
      label: 'Epics',
      value: totalEpics > 0 ? `${completedEpics}/${totalEpics}` : '—',
      sub: 'completed',
      accent: completedEpics === totalEpics && totalEpics > 0 ? 'green' : 'blue',
    },
    {
      label: 'Sprites',
      value: `${activeSprites}`,
      sub: 'active',
      accent: activeSprites > 0 ? 'coral' : 'neutral',
      pulse: activeSprites > 0,
    },
    {
      label: 'Commits',
      value: `${commitCount}`,
      sub: commitCount === 1 ? 'commit' : 'commits',
      accent: commitCount > 0 ? 'blue' : 'neutral',
    },
    {
      label: 'QA Checks',
      value: verificationStats.total > 0 ? `${verificationStats.passed}/${verificationStats.total}` : '—',
      sub: verificationStats.failed > 0 ? `${verificationStats.failed} of ${verificationStats.total} failed` : 'all passed',
      accent: verificationStats.total === 0 ? 'neutral' : verificationStats.failed > 0 ? 'red' : 'green',
    },
    {
      label: 'Story Failures',
      value: `${errorCount}`,
      sub: errorCount > 0 && topErrorMessage
        ? topErrorMessage.slice(0, 40)
        : errorCount === 1 ? 'failure' : 'failures',
      accent: errorCount > 0 ? 'red' : 'green',
    },
    {
      label: 'Elapsed',
      value: elapsed,
      accent: 'neutral',
    },
    // Sprite activity KPI cards (only shown when data exists)
    ...(fileStats.total > 0 ? [{
      label: 'Files Changed',
      value: `${fileStats.total}`,
      sub: `${fileStats.created} created, ${fileStats.modified} modified`,
      accent: 'blue' as KpiCard['accent'],
    }] : []),
    ...(qualityGateStats.total > 0 ? [{
      label: 'Quality Gates',
      value: `${qualityGateStats.passed}/${qualityGateStats.total}`,
      sub: `${qualityGateStats.passRate}% pass rate`,
      accent: (qualityGateStats.passRate >= 80 ? 'green' : 'red') as KpiCard['accent'],
    }] : []),
    // Full-cycle KPI cards (only shown when data exists)
    ...(buildStats.total > 0 ? [{
      label: 'Build Success',
      value: `${buildStats.succeeded}/${buildStats.total}`,
      sub: buildStats.succeeded === buildStats.total ? 'all passed' : `${buildStats.total - buildStats.succeeded} failed`,
      accent: (buildStats.succeeded === buildStats.total ? 'green' : 'red') as KpiCard['accent'],
    }] : []),
    ...(e2eStats.started > 0 ? [{
      label: 'E2E Pass Rate',
      value: `${e2eStats.allPass}/${e2eStats.started}`,
      sub: e2eStats.allPass === e2eStats.started ? 'all passed' : 'some failed',
      accent: (e2eStats.allPass === e2eStats.started ? 'green' : 'red') as KpiCard['accent'],
    }] : []),
    ...(repairCount > 0 ? [{
      label: 'Repair Cycles',
      value: `${repairCount}`,
      sub: repairCount === 1 ? 'cycle' : 'cycles',
      accent: (repairCount > 2 ? 'red' : 'blue') as KpiCard['accent'],
    }] : []),
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 xl:grid-cols-10 gap-2">
      {cards.map((card) => {
        const colors = accentColors[card.accent];
        return (
          <div
            key={card.label}
            className={`relative ${colors.bg} border border-neutral-200/80 border-l-[3px] ${colors.border} rounded px-3 py-2 transition-shadow hover:shadow-warm-sm`}
          >
            <div className="flex items-center gap-1.5">
              <p className="text-[9px] font-bold uppercase tracking-widest text-neutral-400 mb-0.5">
                {card.label}
              </p>
              {card.pulse && (
                <span className="relative flex h-1.5 w-1.5 mb-0.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-coral-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-coral-500" />
                </span>
              )}
            </div>
            <p className={`text-lg font-bold font-mono tabular-nums leading-tight ${colors.value}`}>
              {card.value}
            </p>
            {card.sub && (
              <p className="text-[9px] text-neutral-400 mt-0.5">{card.sub}</p>
            )}
            {/* Mini progress bar for stories */}
            {card.label === 'Stories' && totalStories > 0 && (
              <div className="mt-1.5 h-1 bg-neutral-200/60 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${storiesProgress}%`,
                    background: completedStories === totalStories
                      ? 'var(--color-accent-500, #22c55e)'
                      : 'var(--color-coral-500, #D97F6F)',
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
